/**
 * Defines the runtime-validated vocabulary shared by QEP preload window, iframe, and worker boundaries.
 * Schemas stay composable so boundary listeners can validate an envelope before selecting a payload parser.
 */
import * as v from 'valibot';
import {HasiStatus} from '@qep/common';
import {QEPNotificationPayloadSchema} from '@qep/shared/src/schemas/notifications';

const nonEmptyStringSchema = v.pipe(v.string(), v.minLength(1));

/** Maps every supported renderer routing name to its deployed SharedWorker entry point. */
export const workerDefinitions = {
	healthCheck: 'healthcheck-worker',
	webSocket: 'web-socket-worker',
	mdsWebSocket: 'mds-ws-worker',
	oasWebSocket: 'oas-ws-worker',
} as const;

export type WorkerName = keyof typeof workerDefinitions;

/** Lists worker routing names in the deterministic order used for construction and queue drains. */
export const workerNames = Object.freeze(Object.keys(workerDefinitions) as WorkerName[]);

/** Rejects iframe envelopes that attempt to address a worker outside the supported runtime set. */
export const workerNameSchema = v.picklist(workerNames);

const supportedWorkerNames: ReadonlySet<string> = new Set(workerNames);

/** Narrows an untrusted bridge target to one of the workers owned by this runtime. */
export const isSupportedWorkerName = (workerName: unknown): workerName is WorkerName => (
	typeof workerName === 'string' && supportedWorkerNames.has(workerName)
);

/** Centralizes topics used for renderer-to-worker lifecycle and liveness commands. */
export const workerMessageTopics = {
	sessionIdUpdate: 'sessionIdUpdate',
	internalPong: 'internalPong',
	updateHealthCheck: 'updateHealthCheck',
	windowHealthCheck: 'windowHealthCheck',
	debugSubscriptionListResponse: 'DEBUG:SUBLIST:RESPONSE',
	clientDisconnect: 'clientDisconnect',
} as const;

/** Centralizes discriminators used by renderer subscription-control messages. */
export const workerMessageTypes = {
	subscription: 'subscription',
	unsubscribe: 'unsubscribe',
	windowHealthCheck: 'windowHealthCheck',
	debugSubscriptionListResponse: 'DEBUG:SUBLIST:RESPONSE',
	dispose: 'dispose',
} as const;

const reservedWorkerMessageTopics: ReadonlySet<string> = new Set(Object.values(workerMessageTopics));

/** Rejects subscription topics that a worker dispatches as control commands before reading messageType. */
const isSubscriptionTopic = (topic: string): boolean => !reservedWorkerMessageTopics.has(topic);

const subscriptionTopicSchema = v.pipe(
	nonEmptyStringSchema,
	v.check(isSubscriptionTopic, 'Subscription topics cannot use reserved worker control topics.'),
);

/** Validates the command envelope shape before preserving legacy unsupported-worker diagnostics. */
export const workerIframeCommandCandidateSchema = v.object({
	workerName: v.string(),
	message: v.string(),
});

/** Validates commands sent from the renderer window to the hidden worker iframe. */
export const workerIframeCommandSchema = v.object({
	workerName: workerNameSchema,
	message: v.string(),
});

export type WorkerIframeCommand = v.InferOutput<typeof workerIframeCommandSchema>;

/** Validates an out-of-band request to replace one stale SharedWorker relay port. */
export const workerRelayReplacementCommandSchema = v.object({
	recoveryId: nonEmptyStringSchema,
	workerName: workerNameSchema,
	type: v.literal('replaceWorkerRelay'),
});

export type WorkerRelayReplacementCommand = v.InferOutput<typeof workerRelayReplacementCommandSchema>;

/** Validates the response envelope shape before preserving legacy unsupported-worker diagnostics. */
export const workerIframeResponseCandidateSchema = v.object({
	workerName: v.string(),
	message: v.unknown(),
});

/** Validates responses forwarded from the hidden worker iframe before payload-specific parsing. */
export const workerIframeResponseSchema = v.object({
	workerName: workerNameSchema,
	message: v.unknown(),
});

export type WorkerIframeResponse = v.InferOutput<typeof workerIframeResponseSchema>;

/** Validates the iframe signal emitted after BFCache restoration replaces its SharedWorker ports. */
export const workerRelayRestoredMessageSchema = v.object({
	newRelayGeneration: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1))),
	oldRelayGeneration: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1))),
	recoveryId: v.optional(nonEmptyStringSchema),
	type: v.literal('workerRelayRestored'),
});

export type WorkerRelayRestoredMessage = v.InferOutput<typeof workerRelayRestoredMessageSchema>;

/** Validates authentication changes and reconnect probes shared by websocket workers. */
export const sessionIdUpdateCommandSchema = v.object({
	topic: v.literal(workerMessageTopics.sessionIdUpdate),
	sessionId: v.optional(v.string()),
	messageType: v.optional(v.never()),
});

/** Validates credential synchronization updates before they can replace worker authentication state. */
export const credentialSessionUpdateMessageSchema = v.object({
	topic: v.literal(workerMessageTopics.sessionIdUpdate),
	sessionId: nonEmptyStringSchema,
});

/** Validates renderer liveness acknowledgements consumed by websocket workers. */
export const internalPongCommandSchema = v.object({
	topic: v.literal(workerMessageTopics.internalPong),
	messageType: v.optional(v.never()),
});

/** Validates explicit snapshot requests consumed by the health-check worker. */
export const updateHealthCheckCommandSchema = v.object({
	topic: v.literal(workerMessageTopics.updateHealthCheck),
	messageType: v.optional(v.never()),
});

/** Validates the terminal renderer-port disconnect shared by all QEP worker hosts. */
export const clientDisconnectCommandSchema = v.object({
	topic: v.literal(workerMessageTopics.clientDisconnect),
	messageType: v.literal(workerMessageTypes.dispose),
});

/** Validates every command accepted by the health-check worker port. */
export const healthCheckWorkerCommandSchema = v.union([
	updateHealthCheckCommandSchema,
	clientDisconnectCommandSchema,
]);

/** Validates optional replay behavior retained with a renderer subscription. */
export const subscriptionReplayOptionsSchema = v.object({
	replayStrategy: v.optional(nonEmptyStringSchema),
});

/** Validates the subscription details retained in renderer window-health snapshots. */
export const subscriptionSnapshotEntrySchema = v.object({
	topic: nonEmptyStringSchema,
	data: v.optional(v.unknown()),
	subscriptionOptions: v.optional(subscriptionReplayOptionsSchema),
});

const subscriptionControlBase = {
	topicUniqueId: nonEmptyStringSchema,
	topic: subscriptionTopicSchema,
	data: v.optional(v.unknown()),
	subscriptionOptions: v.optional(subscriptionReplayOptionsSchema),
	requestId: v.optional(nonEmptyStringSchema),
};

/** Validates a renderer request to create or replay a worker subscription. */
export const subscribeCommandSchema = v.object({
	...subscriptionControlBase,
	recoveryId: v.optional(nonEmptyStringSchema),
	messageType: v.literal(workerMessageTypes.subscription),
});

/** Validates a renderer request to remove a worker subscription. */
export const unsubscribeCommandSchema = v.object({
	...subscriptionControlBase,
	messageType: v.literal(workerMessageTypes.unsubscribe),
});

/** Validates the renderer's authoritative subscription snapshot used for worker recovery. */
export const windowHealthCheckCommandSchema = v.object({
	topicUniqueId: v.literal(workerMessageTopics.windowHealthCheck),
	topic: v.literal(workerMessageTopics.windowHealthCheck),
	messageType: v.literal(workerMessageTypes.windowHealthCheck),
	data: v.record(nonEmptyStringSchema, subscriptionSnapshotEntrySchema),
});

/** Validates the optional diagnostic subscription-list response sent through a worker port. */
export const debugSubscriptionListResponseCommandSchema = v.object({
	topicUniqueId: v.literal(workerMessageTopics.debugSubscriptionListResponse),
	topic: v.literal(workerMessageTopics.debugSubscriptionListResponse),
	messageType: v.literal(workerMessageTypes.debugSubscriptionListResponse),
	data: v.object({
		meta: v.optional(v.unknown()),
		subscriptions: v.array(v.unknown()),
	}),
});

/** Validates every supported command after a worker bridge has decoded its JSON string. */
export const websocketWorkerCommandSchema = v.union([
	clientDisconnectCommandSchema,
	sessionIdUpdateCommandSchema,
	internalPongCommandSchema,
	subscribeCommandSchema,
	unsubscribeCommandSchema,
	windowHealthCheckCommandSchema,
	debugSubscriptionListResponseCommandSchema,
]);

/** Selects the command schema enforced by the iframe for a specific worker target. */
export function getWorkerCommandSchema(workerName: WorkerName): typeof healthCheckWorkerCommandSchema | typeof websocketWorkerCommandSchema {
	return workerName === 'healthCheck' ? healthCheckWorkerCommandSchema : websocketWorkerCommandSchema;
}

/** Validates every supported command after a worker bridge has decoded its JSON string. */
export const workerCommandSchema = v.union([
	healthCheckWorkerCommandSchema,
	websocketWorkerCommandSchema,
]);

export type WorkerCommand = v.InferOutput<typeof workerCommandSchema>;
export type SubscriptionControlMessage = v.InferOutput<typeof subscribeCommandSchema | typeof unsubscribeCommandSchema>;

/** Validates subscription frames serialized by Subscription before protocol-specific translation. */
export const subscriptionSocketMessageSchema = v.object({
	topic: nonEmptyStringSchema,
	messageType: v.picklist([workerMessageTypes.subscription, workerMessageTypes.unsubscribe]),
	data: v.unknown(),
	requestId: nonEmptyStringSchema,
});

export type SubscriptionSocketMessage = v.InferOutput<typeof subscriptionSocketMessageSchema>;

/** Validates a decoded webgateway envelope before subscription registries inspect routing fields. */
export const subscriptionServerMessageSchema = v.object({
	requestId: v.optional(nonEmptyStringSchema),
	responseType: v.optional(v.string()),
	topic: v.optional(v.string()),
	topicUniqueId: v.optional(nonEmptyStringSchema),
	payload: v.unknown(),
});

export type SubscriptionServerMessage = v.InferOutput<typeof subscriptionServerMessageSchema>;

/** Validates the server request that asks a worker to publish its active subscription snapshot. */
export const clientSubscriptionsRequestSchema = v.object({
	topic: v.literal('clientSubscriptions'),
	requestId: nonEmptyStringSchema,
});

/** Validates the server diagnostic request forwarded to every connected renderer port. */
export const debugSubscriptionListRequestSchema = v.object({
	topic: v.literal('DEBUG:SUBLIST:REQUEST'),
	meta: v.optional(v.unknown()),
});

/** Limits SubscriptionManager-owned server controls to the two supported request variants. */
export const subscriptionManagerRequestSchema = v.union([
	clientSubscriptionsRequestSchema,
	debugSubscriptionListRequestSchema,
]);

export type SubscriptionManagerRequest = v.InferOutput<typeof subscriptionManagerRequestSchema>;

/** Validates one health status posted from the health-check SharedWorker. */
export const workerHealthStatusSchema = v.object({
	lastHeartbeatUtc: v.string(),
	statusInfo: v.enum(HasiStatus),
	statusInfoString: v.string(),
	componentName: v.string(),
	dependencies: v.optional(v.array(v.unknown())),
});

/** Validates the complete health-check snapshot returned to a renderer facade. */
export const healthCheckWorkerPortResponseSchema = v.array(workerHealthStatusSchema);

/** Validates the nested liveness response posted by websocket worker ports. */
export const workerHeartbeatResponseSchema = v.object({
	type: v.literal('healthcheck'),
	topicUniqueId: v.picklist(['healthcheck', 'connected']),
	message: v.object({
		error: v.null(),
		payload: v.picklist(['ping', 'connected']),
		topicUniqueId: v.picklist(['healthcheck', 'connected']),
	}),
});

/** Validates application subscription data posted by websocket worker ports. */
export const workerDataResponseSchema = v.object({
	type: v.literal('wsmessage'),
	topicUniqueId: v.optional(nonEmptyStringSchema),
	message: v.unknown(),
});

/** Validates worker-to-renderer messages before a worker-specific consumer handles the payload. */
export const websocketWorkerPortResponseSchema = v.union([
	workerHeartbeatResponseSchema,
	workerDataResponseSchema,
]);

/** Validates worker-to-renderer messages before a worker-specific consumer handles the payload. */
export const workerPortResponseSchema = v.union([
	healthCheckWorkerPortResponseSchema,
	websocketWorkerPortResponseSchema,
]);

export type WorkerPortResponse = v.InferOutput<typeof workerPortResponseSchema>;

/** Centralizes SharedWorker request lifecycle discriminators without constraining dynamic application topics. */
export const appMessageTypes = {
	publish: 'publish',
	subscribe: 'subscribe',
	request: 'request',
	response: 'response',
	broadcast: 'broadCast',
} as const;

/** Names the terminal application-iframe notification handled only by its owning SharedClient. */
export const appClientDisconnectTopic = 'client_disconnect';

const appRequestMetaSchema = v.object({requestId: nonEmptyStringSchema});
const appMessageBase = {
	topic: nonEmptyStringSchema,
	message: v.unknown(),
	sender: v.optional(v.string()),
};

/** Validates a correlated request sent across the Browser application SharedWorker bridge. */
export const appRequestMessageSchema = v.object({
	...appMessageBase,
	messageType: v.literal(appMessageTypes.request),
	meta: appRequestMetaSchema,
});

/** Validates a correlated response sent across the Browser application SharedWorker bridge. */
export const appResponseMessageSchema = v.object({
	...appMessageBase,
	messageType: v.literal(appMessageTypes.response),
	meta: appRequestMetaSchema,
});

/** Validates an uncorrelated publish sent across the Browser application SharedWorker bridge. */
export const appPublishMessageSchema = v.object({
	...appMessageBase,
	messageType: v.literal(appMessageTypes.publish),
	meta: v.optional(appRequestMetaSchema),
});

/** Validates a subscription-control envelope retained for public MessageRequest compatibility. */
export const appSubscribeMessageSchema = v.object({
	...appMessageBase,
	messageType: v.literal(appMessageTypes.subscribe),
	meta: v.optional(appRequestMetaSchema),
});

/** Validates a cross-window broadcast sent through the Browser application SharedWorker. */
export const appBroadcastMessageSchema = v.object({
	...appMessageBase,
	messageType: v.literal(appMessageTypes.broadcast),
	meta: v.optional(appRequestMetaSchema),
});

/** Validates every supported Browser application SharedWorker request lifecycle variant. */
export const appMessageSchema = v.variant('messageType', [
	appRequestMessageSchema,
	appResponseMessageSchema,
	appPublishMessageSchema,
	appSubscribeMessageSchema,
	appBroadcastMessageSchema,
]);

export type AppMessage = v.InferOutput<typeof appMessageSchema>;

/** Centralizes lifecycle discriminators exchanged before iframe and worker bridges are ready. */
export const appLifecycleMessageTypes = {
	windowInitialize: 'window_initialize',
} as const;

/** Validates the hidden iframe signal that its Browser application SharedWorker relay is online. */
export const appWorkerReadyMessageSchema = v.object({
	workerLoaded: v.literal(true),
});

export type AppWorkerReadyMessage = v.InferOutput<typeof appWorkerReadyMessageSchema>;

/** Validates a component iframe's request for Browser shell initialization context. */
export const windowInitializeRequestSchema = v.object({
	type: v.literal(appLifecycleMessageTypes.windowInitialize),
});

export type WindowInitializeRequest = v.InferOutput<typeof windowInitializeRequestSchema>;

/** Validates the runtime configuration nested in a Browser shell initialization response. */
export const windowRuntimeConfigSchema = v.object({
	apiServer: v.string(),
	appEnv: v.string(),
	fileServer: v.string(),
	previewMode: v.string(),
	webSocketServer: v.string(),
	mdsWsServer: v.string(),
	oasWsServer: v.string(),
});

/** Validates Browser shell context returned to a requesting component iframe. */
export const windowInitializeResponseSchema = v.object({
	type: v.literal(appLifecycleMessageTypes.windowInitialize),
	browserContextId: nonEmptyStringSchema,
	spawnDataId: v.nullable(v.string()),
	// eslint-disable-next-line @typescript-eslint/naming-convention -- Preserve the established cross-window wire field.
	windowID: v.nullable(v.string()),
	componentId: v.nullable(v.string()),
	runtimeConfig: windowRuntimeConfigSchema,
});

export type WindowInitializeResponse = v.InferOutput<typeof windowInitializeResponseSchema>;

/** Validates the Browser shell command that adds an internal Order Blotter tab. */
export const orderBlotterAddTabMessageSchema = v.object({
	type: v.literal('order_blotter_add_internal_tab'),
});

/** Centralizes message types posted by preload notification APIs to the Browser shell. */
export const notificationMessageTypes = {
	notification: 'qep.notification',
	localToast: 'qep.notification.local.toast',
	localDismiss: 'qep.notification.local.dismiss',
} as const;

/** Lists the supported delegated Noty methods consumed by the Browser shell. */
export const localNotificationMethods = ['error', 'success', 'warning', 'info'] as const;

/** Validates a notification that the Browser shell persists in its notification center. */
export const notificationMessageSchema = v.object({
	type: v.literal(notificationMessageTypes.notification),
	data: QEPNotificationPayloadSchema,
});

/** Validates a child-frame request for the Browser shell to render a delegated toast. */
export const localNotificationToastMessageSchema = v.object({
	type: v.literal(notificationMessageTypes.localToast),
	data: v.object({
		id: nonEmptyStringSchema,
		method: v.picklist(localNotificationMethods),
		title: nonEmptyStringSchema,
	}),
});

/** Validates a child-frame request for the Browser shell to close a delegated toast. */
export const localNotificationDismissMessageSchema = v.object({
	type: v.literal(notificationMessageTypes.localDismiss),
	data: v.object({id: nonEmptyStringSchema}),
});

/** Validates every supported notification message posted from QEP preloads to the Browser shell. */
export const notificationPostMessageSchema = v.variant('type', [
	notificationMessageSchema,
	localNotificationToastMessageSchema,
	localNotificationDismissMessageSchema,
]);

export type NotificationPostMessage = v.InferOutput<typeof notificationPostMessageSchema>;
export type NotificationMessage = v.InferOutput<typeof notificationMessageSchema>;
export type LocalNotificationToastMessage = v.InferOutput<typeof localNotificationToastMessageSchema>;
export type LocalNotificationDismissMessage = v.InferOutput<typeof localNotificationDismissMessageSchema>;
